import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../config/api";

const USE_MOCK = process.env.REACT_APP_USE_MOCK === "true";

// ── Types ─────────────────────────────────────────────────────────────────────
export type UserRole = "admin" | "member" | "viewer";

export interface User {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  company_id?: string;
  personal_workspace_id?: string;
  active_workspace_id?: string;
  country?: string;
  role?: UserRole;
  plan?: string;
}

export interface Workspace {
  id: string;
  name: string;
  role: UserRole;
  is_personal: boolean;
  member_count?: number;
}

interface AuthContextType {
  user: User | null;
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  checkEmail: (email: string) => Promise<{ exists: boolean }>;
  register: (data: RegisterData) => Promise<{ message: string }>;
  requestMagicLink: (
    email: string
  ) => Promise<{ message: string; autoLoggedIn?: boolean }>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  company_name?: string;
  email: string;
  country?: string;
  timezone?: string;
}

// ── Mock user for development ─────────────────────────────────────────────────
const MOCK_USER: User = {
  id: "00000000-0000-0000-0001-000000000099",
  email: "demo@company.com",
  name: "Demo User",
  first_name: "Demo",
  last_name: "User",
  company_name: "Acme Inc.",
  role: "admin",
};
const MOCK_TOKEN = "mock-jwt-token-dev-only";

// ── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Boot: restore session from localStorage ───────────────────────────────
  useEffect(() => {
    if (USE_MOCK) {
      if (!localStorage.getItem("token")) {
        localStorage.setItem("token", MOCK_TOKEN);
        localStorage.setItem("user", JSON.stringify(MOCK_USER));
      }
      const stored = localStorage.getItem("user");
      const u = stored ? JSON.parse(stored) : MOCK_USER;
      setUser(u);
      setActiveWorkspaceId(u.active_workspace_id ?? u.company_id ?? null);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    const stored = localStorage.getItem("user");

    if (token && stored) {
      const storedUser = JSON.parse(stored) as User;
      setUser(storedUser);
      setActiveWorkspaceId(
        storedUser.active_workspace_id ?? storedUser.company_id ?? null
      );
      api
        .get("/auth/me")
        .then((res) => {
          const { token: freshToken, workspaces: wsList, ...userData } = res.data;
          if (freshToken) {
            localStorage.setItem("token", freshToken);
          }
          setUser(userData);
          setActiveWorkspaceId(userData.active_workspace_id ?? userData.company_id ?? null);
          setWorkspaces(wsList ?? []);
          localStorage.setItem("user", JSON.stringify(userData));
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
          setWorkspaces([]);
          setActiveWorkspaceId(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ── Step 1: check if email is already registered ──────────────────────────
  const checkEmail = async (email: string): Promise<{ exists: boolean }> => {
    const res = await api.post("/auth/check-email", { email });
    return res.data;
  };

  // ── Step 2a: new user — collect profile + create account ─────────────────
  const register = async (data: RegisterData): Promise<{ message: string }> => {
    const res = await api.post("/auth/register", data);
    // Account is created; magic link sent. Do NOT set user/token yet.
    return res.data;
  };

  // ── Step 2b: returning user — request magic link (or auto-login for demo_users) ──
  const requestMagicLink = async (
    email: string
  ): Promise<{ message: string; autoLoggedIn?: boolean }> => {
    const res = await api.post("/auth/request-magic-link", { email });
    if (res.data.isDemoUser && res.data.token && res.data.user) {
      const u = res.data.user as User;
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(u));
      setUser(u);
      setActiveWorkspaceId(u.active_workspace_id ?? u.company_id ?? null);
      return { message: "Signed in", autoLoggedIn: true };
    }
    return res.data;
  };

  // ── Step 3: verify magic link token (called from /auth/verify page) ───────
  const verifyMagicLink = async (token: string): Promise<void> => {
    const res = await api.get(`/auth/verify?token=${token}`);
    const { user: u, token: jwt } = res.data as { user: User; token: string };
    localStorage.setItem("token", jwt);
    localStorage.setItem("user", JSON.stringify(u));
    setUser(u);
    setActiveWorkspaceId(u.active_workspace_id ?? u.company_id ?? null);
    // Fetch workspaces after login
    try {
      const wsRes = await api.get("/team/my-workspaces");
      setWorkspaces(wsRes.data ?? []);
    } catch {
      // Non-critical — switcher will be empty
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setWorkspaces([]);
    setActiveWorkspaceId(null);
  };

  // ── Switch workspace ──────────────────────────────────────────────────────
  const switchWorkspace = async (workspaceId: string): Promise<void> => {
    const res = await api.post(`/team/switch/${workspaceId}`);
    const { token: freshToken, active_workspace_id, role, plan } = res.data as {
      token: string;
      active_workspace_id: string;
      workspace_name: string;
      role: string;
      plan: string;
    };
    localStorage.setItem("token", freshToken);
    setActiveWorkspaceId(active_workspace_id);
    // Update user role and plan in context — plan belongs to the workspace, not the user
    setUser((prev) =>
      prev
        ? { ...prev, active_workspace_id, role: role as UserRole, plan }
        : prev
    );
    // Update localStorage user
    const stored = localStorage.getItem("user");
    if (stored) {
      const u = JSON.parse(stored) as User;
      localStorage.setItem(
        "user",
        JSON.stringify({ ...u, active_workspace_id, role, plan })
      );
    }
  };

  // ── Refresh workspace list ────────────────────────────────────────────────
  const refreshWorkspaces = async (): Promise<void> => {
    try {
      const res = await api.get("/team/my-workspaces");
      setWorkspaces(res.data ?? []);
    } catch {
      // silently ignore
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        workspaces,
        activeWorkspaceId,
        isAuthenticated: !!user,
        loading,
        checkEmail,
        register,
        requestMagicLink,
        verifyMagicLink,
        logout,
        switchWorkspace,
        refreshWorkspaces,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

const ROLE_RANK: Record<UserRole, number> = { viewer: 1, member: 2, admin: 3 };

/** Returns helpers for role-based UI control */
export function useRole() {
  const { user } = useAuth();
  const role: UserRole = (user?.role as UserRole) || "member";
  const isAdmin = role === "admin";
  const isMember = ROLE_RANK[role] >= ROLE_RANK["member"];
  const isViewer = role === "viewer";
  const canManageTeam = isAdmin;
  const canEdit = isMember; // member + admin
  const canDelete = isMember;
  return {
    role,
    isAdmin,
    isMember,
    isViewer,
    canManageTeam,
    canEdit,
    canDelete,
  };
}
